import type { CreateCallOffPayload, CreateCallOffResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  zodCreateCallOffPayloadSchema,
  zodCreateCallOffResponseSchema,
} from './validations';

/**
 * Creates a new call-off against a confirmed contract.
 * The call-off starts in DRAFT status.
 *
 * @param payload - The call-off data to create.
 * @returns The created call-off with its generated ID and metadata.
 * @throws Error if the API request fails or validation fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/create_calloff_calloffs_post
 */
export const createCallOff = async (payload: CreateCallOffPayload) => {
  const response = await vestigasApi
    .post(ENDPOINT.CALL_OFF.CREATE(), {
      json: payload,
      validate: {
        body: zodCreateCallOffPayloadSchema,
      },
    })
    .json<CreateCallOffResponse>();

  // Validate the response with Zod
  const validatedResponse = zodCreateCallOffResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Create CallOff API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
